import { localApiClient } from "./localApiClient";

const norm = (value) => String(value || "").trim();

const currentHospitalId = () =>
  sessionStorage.getItem("hospitalId") ||
  localStorage.getItem("hospitalId") ||
  "";

const wrapLocalError = (action, error) => {
  const message = error?.message || String(error);
  if (/bearer|jwt|unauthor/i.test(message)) throw new Error(`${action} failed: Local API token is missing or expired.`);
  if (/hospital|scope|forbidden/i.test(message)) throw new Error(`${action} failed: Hospital scope mismatch.`);
  if (/failed to fetch|network/i.test(message)) throw new Error(`${action} failed: Local API is unavailable.`);
  throw new Error(`${action} failed: ${message}`);
};

export const localSettingsService = {
  async fetchSettingsFromFirestore(hospitalId) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      return localApiClient.get(`/settings/general/${encodeURIComponent(hospitalId)}`);
    } catch (error) {
      wrapLocalError("Fetch settings", error);
    }
  },

  async saveSettingsToFirestore(hospitalId, settings = {}, user) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      const saved = await localApiClient.post("/settings/general", {
        hospitalId,
        ...settings,
      });
      return {
        success: true,
        message: "Settings saved successfully.",
        data: saved,
        hospitalDetails: {
          hospitalId,
          hospitalName: saved?.clinicDetails?.name || saved?.hospitalName || "",
          address: saved?.clinicDetails?.address || "",
          phone: saved?.clinicDetails?.phone || "",
          email: saved?.clinicDetails?.email || "",
          logo: saved?.branding?.logo || null,
          updatedBy: user || null,
        },
      };
    } catch (error) {
      wrapLocalError("Save settings", error);
    }
  },

  async fetchHospitalSettings(hospitalId) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      const profile = await localApiClient.get(`/settings/hospital-profile/${encodeURIComponent(hospitalId)}`);
      return {
        ...profile,
        hospitalName: profile.hospitalName || profile.name,
        name: profile.name || profile.hospitalName,
        address: profile.clinicDetails?.address || profile.address || "",
        phone: profile.clinicDetails?.phone || profile.phone || "",
        email: profile.clinicDetails?.email || profile.email || "",
        logo: profile.branding?.logo || profile.reportHeader?.logoUrl || null,
        patientIdPrefix: profile.clinicDetails?.patientIdPrefix || hospitalId,
        invoicePrefix: profile.clinicDetails?.invoicePrefix || hospitalId,
      };
    } catch (error) {
      wrapLocalError("Fetch hospital profile", error);
    }
  },

  async fetchModuleSettings(hospitalId = currentHospitalId()) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      return localApiClient.get(`/settings/modules/${encodeURIComponent(hospitalId)}`);
    } catch (error) {
      wrapLocalError("Fetch module settings", error);
    }
  },

  async saveModuleSettings(hospitalId, modulesEnabled) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      return localApiClient.post("/settings/modules", { hospitalId, modulesEnabled });
    } catch (error) {
      wrapLocalError("Save module settings", error);
    }
  },

  async fetchNumberFormats(hospitalId = currentHospitalId()) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      return localApiClient.get(`/settings/number-formats/${encodeURIComponent(hospitalId)}`);
    } catch (error) {
      wrapLocalError("Fetch number formats", error);
    }
  },

  async fetchReportHeader(hospitalId = currentHospitalId()) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      return localApiClient.get(`/settings/report-header/${encodeURIComponent(hospitalId)}`);
    } catch (error) {
      wrapLocalError("Fetch report header", error);
    }
  },

  async saveReportHeader(hospitalId, payload = {}) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      return localApiClient.post("/settings/report-header", { hospitalId, ...payload });
    } catch (error) {
      wrapLocalError("Save report header", error);
    }
  },

  async createServiceCategory(hospitalId, category = {}) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      return localApiClient.post("/settings/service-categories", {
        hospitalId,
        name: norm(category.name || category.serviceCategory || category.category),
        code: norm(category.code || category.serviceCategoryCode),
        description: norm(category.description),
        departmentName: norm(category.departmentName || category.department),
        metadata: category.metadata || {},
      });
    } catch (error) {
      wrapLocalError("Create service category", error);
    }
  },

  async fetchServiceCategories(hospitalId = currentHospitalId(), filters = {}) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      return localApiClient.get(`/settings/service-categories/${encodeURIComponent(hospitalId)}`, filters);
    } catch (error) {
      wrapLocalError("Fetch service categories", error);
    }
  },

  async updateServiceCategory(hospitalId, id, category = {}) {
    try {
      if (!hospitalId || !id) throw new Error("Hospital ID and category ID are required.");
      return localApiClient.patch(`/settings/service-categories/${encodeURIComponent(hospitalId)}/${encodeURIComponent(id)}`, {
        name: category.name || category.serviceCategory || category.category,
        code: category.code || category.serviceCategoryCode,
        description: category.description,
        departmentName: category.departmentName || category.department,
        status: category.status,
        metadata: category.metadata,
      });
    } catch (error) {
      wrapLocalError("Update service category", error);
    }
  },

  async deleteServiceCategory(hospitalId, id) {
    try {
      if (!hospitalId || !id) throw new Error("Hospital ID and category ID are required.");
      return localApiClient.delete(`/settings/service-categories/${encodeURIComponent(hospitalId)}/${encodeURIComponent(id)}`);
    } catch (error) {
      wrapLocalError("Disable service category", error);
    }
  },
};

export default localSettingsService;
